import { useEffect, useRef, useState } from 'react';
import { esErrorApi } from '../api';
import { obtenerPdf } from './pdf';

/**
 * Ver y descargar el PDF de un comprobante (KAN-147).
 *
 * # POR QUÉ SON BOTONES Y NO ENLACES
 *
 * Un `<a href="/api/v1/comprobantes/42/pdf">` no funcionaría: el navegador no
 * manda el header `Authorization` al seguir un enlace, y la sesión de este
 * front vive sólo en memoria — no hay cookie que viaje sola. El PDF se baja con
 * el cliente HTTP (ver `pdf.ts`) y recién después se abre o se guarda.
 *
 * # LAS URL DE OBJETO SE LIBERAN
 *
 * Cada `URL.createObjectURL` retiene el blob entero en memoria hasta que
 * alguien lo revoca. Un PDF son cientos de kilobytes y esta pantalla se visita
 * muchas veces por sesión, así que se llevan anotadas y se revocan **al
 * desmontar**, no antes: revocar apenas se abre la pestaña dejaría al visor sin
 * el archivo que está mostrando.
 */
export function BotonesDePdf({ id }: { id: number }) {
  const [trabajando, setTrabajando] = useState<'ver' | 'descargar' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const urls = useRef<string[]>([]);

  useEffect(() => {
    const creadas = urls.current;
    return () => {
      creadas.forEach((url) => URL.revokeObjectURL(url));
      creadas.length = 0;
    };
  }, []);

  async function conElPdf(que: 'ver' | 'descargar'): Promise<void> {
    setTrabajando(que);
    setError(null);
    try {
      const pdf = await obtenerPdf(id);
      const url = URL.createObjectURL(pdf.blob);
      urls.current.push(url);
      if (que === 'ver') {
        window.open(url, '_blank', 'noopener');
      } else {
        descargar(url, pdf.nombre);
      }
    } catch (causa) {
      setError(mensajeDePdf(causa));
    } finally {
      setTrabajando(null);
    }
  }

  return (
    <>
      <p>
        <button type="button" onClick={() => void conElPdf('ver')} disabled={trabajando !== null}>
          {trabajando === 'ver' ? 'Abriendo…' : 'Ver el PDF'}
        </button>{' '}
        <button
          type="button"
          onClick={() => void conElPdf('descargar')}
          disabled={trabajando !== null}
        >
          {trabajando === 'descargar' ? 'Bajando…' : 'Descargar el PDF'}
        </button>
      </p>
      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}
    </>
  );
}

/**
 * Dispara el "guardar como" del navegador.
 *
 * El `download` lleva el nombre **que mandó el backend**, no uno armado acá: es
 * el mismo archivo que recibe un sistema conectado que baja el comprobante por
 * API, y que se llame distinto según quién lo bajó sería un problema el día que
 * alguien compare dos carpetas.
 */
function descargar(url: string, nombre: string): void {
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
}

/**
 * Qué se muestra cuando el PDF no se pudo traer.
 *
 * El `403` merece su propio texto: en este endpoint significa que la credencial
 * no alcanza ese tenant, y confundirlo con "no existe" mandaría a buscar un
 * comprobante que sí está.
 */
function mensajeDePdf(causa: unknown): string {
  if (!esErrorApi(causa)) {
    return 'No se pudo abrir el PDF. Probá de nuevo.';
  }
  switch (causa.codigo) {
    case 'tenant_no_autorizado':
    case 'prohibido':
      return 'Tu credencial no puede ver el PDF de ese contribuyente.';
    case 'no_encontrado':
      return 'Ese comprobante ya no está disponible.';
    case 'no_autenticado':
      return 'Tu sesión ya no vale. Volvé a entrar.';
    case 'sin_conexion':
      return 'No se pudo contactar al servicio. Esto solo descarga, así que podés reintentar sin riesgo.';
    default:
      return 'No se pudo generar el PDF. Probá de nuevo en un momento.';
  }
}
